import {Component, computed, DestroyRef, inject, linkedSignal, signal, viewChild} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {StoreEmployeeRequirement} from '../../../../classes/models/store/StoreEmployeeRequirement';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {DataService} from '../../../../services/store/data.service';
import {StoreEmployeeRequirementService} from '../../../../services/http/store/store-employee-requirement.service';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * Data required to open the employee requirements form modal.
 */
export interface EmployeeRequirementsFormData {
  storeId: string;
  previousRequirement?: StoreEmployeeRequirement;
  weekExists: boolean;
}

/**
 * This component handles creating or updating store requirements.
 */
@Component({
  selector: 'app-employee-requirements-form',
  templateUrl: './employee-requirements-form.component.html',
  styleUrls: ['./employee-requirements-form.component.less'],
  imports: [SHARED_IMPORTS]
})
export class EmployeeRequirementsFormComponent {

  private readonly storeEmployeeRequirementService = inject(StoreEmployeeRequirementService);
  private readonly toastr = inject(ToastrService);
  private readonly ruleCheckerService = inject(RuleCheckerService);
  private readonly dataService = inject(DataService);
  private readonly destroyRef = inject(DestroyRef);

  /** A modal component of a store requirement form. */
  readonly modalComponent = viewChild<ModalComponent>('modalComponent');
  /** An identifier of a store. */
  readonly storeId = signal<string>('');
  /** Previous special store employee requirements. */
  readonly previousRequirement = signal<StoreEmployeeRequirement | undefined>(undefined);
  /** `True` if there are records of the selected week in backend. */
  readonly weekExists = signal(false);

  /** A signal form value of start date for new store requirement, linked to previous requirement signal. */
  readonly startDate = linkedSignal(() => this.previousRequirement()?.startDate ?? '');
  /** A signal form value of a minimum employees needed to be present at the store opening, linked to previous requirement signal. */
  readonly minimumOpeningEmployees = linkedSignal<number | undefined>(() => this.previousRequirement()?.minimumOpeningEmployees);
  /** A signal form value of a minimum employees needed to be present at regular working hours, linked to previous requirement signal. */
  readonly minimumRegularEmployees = linkedSignal<number | undefined>(() => this.previousRequirement()?.minimumRegularEmployees);
  /** A signal form value of a minimum employees needed to be present at the store closing, linked to previous requirement signal. */
  readonly minimumClosingEmployees = linkedSignal<number | undefined>(() => this.previousRequirement()?.minimumClosingEmployees);
  /** `True` if request to backend is in progress. */
  readonly loading = signal(false);
  /** The first date from which the data can be edited, derived from data service. */
  readonly editDate = this.dataService.editDate;

  static open(modalService: NgbModal, data: EmployeeRequirementsFormData): NgbModalRef {
    const modalRef = modalService.open(EmployeeRequirementsFormComponent);
    const instance = modalRef.componentInstance as EmployeeRequirementsFormComponent;
    instance.storeId.set(data.storeId);
    instance.previousRequirement.set(data.previousRequirement);
    instance.weekExists.set(data.weekExists);
    return modalRef;
  }

  /**
   * Sends request to backend to create or update an employee store requirement.
   */
  onSubmit(): void {
    this.loading.set(true);

    const requirement = new StoreEmployeeRequirement(
            this.startDate(),
            this.minimumOpeningEmployees(),
            this.minimumRegularEmployees(),
            this.minimumClosingEmployees()
    );

    if (this.weekExists()) {
      this.editRequirement(requirement);
    } else {
      this.addRequirement(requirement);
    }
  }

  private addRequirement(requirement: StoreEmployeeRequirement): void {
    this.storeEmployeeRequirementService.addRequirement(this.storeId(), requirement)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent()?.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Requirements were successfully created');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  private editRequirement(requirement: StoreEmployeeRequirement): void {
    this.storeEmployeeRequirementService.editRequirement(this.storeId(), requirement)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent()?.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Requirements were successfully edited');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}